import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Chapter } from "@/components/Chapter";
import { AppPreview } from "@/components/AppPreview";
import { Support } from "@/components/Support";
import { Footer } from "@/components/Footer";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import UniversalLinks from "@/pages/UniversalLinks";
import ChildSafety from "@/pages/ChildSafety";
import DeepLink from "@/pages/DeepLink";

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const CHAPTERS = [
  {
    number: "01",
    kicker: "Salute",
    title: "Il libretto sanitario, sempre in tasca.",
    description:
      "Vaccini, visite, peso e promemoria: tutta la storia clinica del tuo cane in un unico posto. FuffyFam ti avvisa prima di ogni scadenza, così non ne perdi nemmeno una.",
    points: ["Libretto sanitario digitale", "Promemoria vaccini e antiparassitari", "Storico peso e crescita"],
    image:
      "https://images.unsplash.com/photo-1557495235-340eb888a9fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkb2clMjBhbmQlMjBvd25lciUyMGh1Z2dpbmd8ZW58MHx8fHwxNzg4ODk5MjA5fDA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Una ragazza abbraccia il suo cane",
    reverse: false,
  },
  {
    number: "02",
    kicker: "Sitter & Strutture",
    title: "Persone fidate, posti a misura di cane.",
    description:
      "Trova dog sitter verificati nella tua zona e scopri hotel, spiagge e ristoranti dog-friendly. Recensioni reali dalla community, per partire senza pensieri.",
    points: ["Dog sitter verificati vicino a te", "Mappa delle strutture dog-friendly", "Recensioni dalla community"],
    image:
      "https://images.unsplash.com/photo-1522276498395-f4f68f7f8454?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxkb2clMjBhbmQlMjBvd25lciUyMGh1Z2dpbmd8ZW58MHx8fHwxNzg4ODk5MjA5fDA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Un uomo abbraccia il suo cane all'aperto",
    reverse: true,
  },
  {
    number: "03",
    kicker: "Community",
    title: "Nuovi amici a quattro zampe.",
    description:
      "Incontra altri proprietari e organizza passeggiate nella tua zona. Perché anche il tuo cane merita una famiglia allargata — e magari qualche amico con cui correre al parco.",
    points: ["Cani e proprietari nella tua zona", "Passeggiate ed eventi di gruppo", "Chat e gruppi per razza e città"],
    image:
      "https://images.unsplash.com/photo-1607696412329-265a4103b433?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxkb2clMjBydW5uaW5nJTIwcGFya3xlbnwwfHx8fDE3ODg4OTkyMDl8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Un cane corre felice al parco",
    reverse: false,
  },
];

function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (window.location.hash === "#unisciti") {
      document.getElementById("unisciti")?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A1A] antialiased overflow-x-clip">
      <div
        aria-hidden="true"
        className="fixed inset-0 z-50 pointer-events-none opacity-[0.18] mix-blend-multiply"
        style={{ backgroundImage: GRAIN }}
      />
      <Hero />
      <Marquee />
      <main data-testid="manifesto">
        {CHAPTERS.map((c) => (
          <Chapter key={c.number} {...c} />
        ))}
      </main>
      <AppPreview />
      <Marquee reverse />
      <Support />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/universal-links" element={<UniversalLinks />} />
        <Route path="/child-safety" element={<ChildSafety />} />
        <Route path="/u/:id" element={<DeepLink />} />
        <Route path="/cane/:id" element={<DeepLink />} />
        <Route path="/partner/:id" element={<DeepLink />} />
        <Route path="*" element={<DeepLink />} />
      </Routes>
      <Toaster position="bottom-center" toastOptions={{ style: { background: "#1A1A1A", color: "#F9F8F6", border: "none" } }} />
    </BrowserRouter>
  );
}

export default App;
